import { type EnvironmentId, type MachinePoolAddInput, WS_METHODS } from "@afar-research/contracts";
import * as Effect from "effect/Effect";
import type { Atom } from "effect/unstable/reactivity";

import { EnvironmentRegistry } from "../connection/registry.ts";
import { request } from "../rpc/client.ts";
import { createAtomCommandScheduler, createRuntimeCommand } from "./runtime.ts";

/**
 * A project's Machine Pool (ADR-0149), managed from a client connected to both ends: the
 * destination issues a delegation grant, and the project's own Environment redeems it there.
 */
export function createMachinePoolAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const scheduler = createAtomCommandScheduler();
  const list = createRuntimeCommand(runtime, {
    label: "machine-pool:list",
    scheduler,
    execute: (input: { readonly environmentId: EnvironmentId; readonly projectId: string }) =>
      Effect.gen(function* () {
        const registry = yield* EnvironmentRegistry;
        return yield* registry.run(
          input.environmentId,
          request(WS_METHODS.machinePoolList, { projectId: input.projectId }),
        );
      }),
  });

  const add = createRuntimeCommand(runtime, {
    label: "machine-pool:add",
    scheduler,
    concurrency: { mode: "serial", key: () => "machine-pool" },
    /** Asks the destination for a delegation grant, then the project's Environment to redeem it. */
    execute: (input: {
      readonly environmentId: EnvironmentId;
      readonly destinationEnvironmentId: EnvironmentId;
      readonly entry: Omit<MachinePoolAddInput, "grant">;
    }) =>
      Effect.gen(function* () {
        const registry = yield* EnvironmentRegistry;
        const delegation = yield* registry.run(
          input.destinationEnvironmentId,
          request(WS_METHODS.machinePoolIssueDelegation, {
            projectId: input.entry.remoteProjectId,
          }),
        );
        return yield* registry.run(
          input.environmentId,
          request(WS_METHODS.machinePoolAdd, { ...input.entry, grant: delegation.grant }),
        );
      }),
  });

  const remove = createRuntimeCommand(runtime, {
    label: "machine-pool:remove",
    scheduler,
    concurrency: { mode: "serial", key: () => "machine-pool" },
    execute: (input: {
      readonly environmentId: EnvironmentId;
      readonly projectId: string;
      readonly destinationEnvironmentId: string;
    }) =>
      Effect.gen(function* () {
        const registry = yield* EnvironmentRegistry;
        return yield* registry.run(
          input.environmentId,
          request(WS_METHODS.machinePoolRemove, {
            projectId: input.projectId,
            environmentId: input.destinationEnvironmentId,
          }),
        );
      }),
  });

  return { list, add, remove };
}
